/* eslint-disable react/prop-types, consistent-return, react-hooks/exhaustive-deps -- pre-existing legacy map shape (no propTypes, imperative gmaps effects); this change only adds the dashed option */
import { useMap, useMapsLibrary, useApiIsLoaded } from '@vis.gl/react-google-maps'
import { useState, useEffect } from 'react'

import { filterValidLatLngs } from 'components/common/map/constants'
import { COMMON } from 'theme/colors'

const Polyline = props => {
  // Destructure path specifically so we can use it in a dedicated effect
  const {
    encodedPath,
    path,
    onClick,
    onMouseOver,
    onMouseMove,
    onMouseOut,
    showArrows = false,
    arrowStyle = 'solid',
    arrowRepeat = '50px',
    dashed = false,
    ...polylineOptions
  } = props

  const map = useMap()
  const geometryLibrary = useMapsLibrary('geometry')
  const mapsLibrary = useMapsLibrary('maps')
  const apiIsLoaded = useApiIsLoaded()

  const [polyline, setPolyline] = useState(null)

  // --- Helpers ---
  const createArrowSymbol = () => {
    if (!window.google || !window.google.maps) return null

    const weight = polylineOptions.strokeWeight || 2
    const opacity = polylineOptions.strokeOpacity ?? 1
    const strokeColor = polylineOptions.strokeColor || COMMON.black

    if (arrowStyle === 'chevron') {
      return {
        path: 'M -3,1 0,-2 3,1',
        scale: weight > 4 ? 3 : 2.5,
        strokeColor,
        strokeWeight: 1.5,
        strokeOpacity: opacity,
        fillOpacity: 0,
      }
    }

    // Longer than it is wide, so the apex reads as a direction rather than a dot.
    return {
      path: 'M 0,-5.5 L 4.5,3 L -4.5,3 Z',
      scale: weight > 4 ? 1.5 : 1.25,
      fillColor: strokeColor,
      fillOpacity: 1,
      strokeWeight: 0,
    }
  }

  // --- 1. Create Instance ---
  useEffect(() => {
    if (!mapsLibrary || !apiIsLoaded) return
    const instance = new mapsLibrary.Polyline()
    setPolyline(instance)

    return () => {
      instance.setMap(null)
    }
  }, [mapsLibrary, apiIsLoaded])

  // --- 2. Update Options ---
  useEffect(() => {
    if (!polyline || !mapsLibrary || !apiIsLoaded) return

    const updatedOptions = { ...polylineOptions }
    const icons = []

    // Dashed lines: Google Maps has no native dashArray, so hide the solid
    // stroke and repeat a short dash symbol along the path (same technique as
    // the bulk maps' RoutePolyline) to match the dotted sequence-line style.
    if (dashed) {
      icons.push({
        icon: {
          path: 'M 0,-1 0,1',
          strokeColor: polylineOptions.strokeColor || COMMON.black,
          strokeOpacity: polylineOptions.strokeOpacity ?? 1,
          scale: polylineOptions.strokeWeight || 2,
        },
        offset: '0',
        repeat: '12px',
      })
      updatedOptions.strokeOpacity = 0
    }

    if (showArrows) {
      const arrowSymbol = createArrowSymbol()
      if (arrowSymbol) icons.push({ icon: arrowSymbol, offset: '0', repeat: arrowRepeat })
    }

    updatedOptions.icons = icons
    updatedOptions.clickable = !!(onClick || onMouseOver || onMouseMove)

    polyline.setOptions(updatedOptions)
  }, [polyline, polylineOptions, mapsLibrary, apiIsLoaded, onClick, onMouseOver, onMouseMove, showArrows, arrowStyle, arrowRepeat, dashed])

  // --- 3A. Handle Encoded Path (Sequence Lines) ---
  useEffect(() => {
    if (!encodedPath || !geometryLibrary || !polyline || !apiIsLoaded) return
    const decodedPath = geometryLibrary.encoding.decodePath(encodedPath)
    polyline.setPath(decodedPath)
  }, [polyline, encodedPath, geometryLibrary, apiIsLoaded])

  // --- 3B. Handle Direct Path Array (Breadcrumbs) ---
  // FIX: This ensures raw coordinate arrays are actually rendered
  useEffect(() => {
    if (!path || !polyline || !apiIsLoaded) return
    polyline.setPath(filterValidLatLngs(path))
  }, [polyline, path, apiIsLoaded])

  // --- 4. Bind Map ---
  useEffect(() => {
    if (!map || !polyline || !apiIsLoaded) return
    polyline.setMap(map)
  }, [map, polyline, apiIsLoaded])

  // --- 5. Event Listeners ---
  useEffect(() => {
    if (!polyline) return

    const listeners = []

    if (onClick) listeners.push(polyline.addListener('click', onClick))
    if (onMouseOver) listeners.push(polyline.addListener('mouseover', onMouseOver))
    if (onMouseMove) listeners.push(polyline.addListener('mousemove', onMouseMove))
    if (onMouseOut) listeners.push(polyline.addListener('mouseout', onMouseOut))

    return () => {
      listeners.forEach(l => l.remove())
    }
  }, [polyline, onClick, onMouseOver, onMouseMove, onMouseOut])

  return null
}

export default Polyline
